import React, { useEffect, useState } from "react";
import MedicationBlock from "./MedicationBlock";
import PatientInfoCard from "./PatientInfoCard";
import { useFormik } from "formik";
import { DatePicker } from "rsuite";
import { Info } from 'lucide-react';
import { Popover, Whisper } from 'rsuite';
import "rsuite/dist/rsuite.min.css";
import dayjs from "dayjs";
import { MdOutlineDeleteOutline } from "react-icons/md";
import {
  useLazyGetBookedPatientsPrescriptionDropdownQuery,
  useAddPrescriptionMutation,
} from "../State/Api/AddPrescriptionApiSlice";
import {
  useAddTreatmentPlanMutation,
  useLazyGetTreatmentPlansQuery,
} from "../State/Api/TreatmentPlanApiSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function ClinicPrescriptionForm() {
  const location = useLocation();
  const patientUserUniqueID = location.state?.PatientuserUniqueID;
  const appointmentDate = location.state?.appointmentDate;
  const appointmentID = location.state?.appointmentID;
  const userClinicID = location.state?.userClinicID;
  const editData = location?.state?.editData;

  const isEditMode = Boolean(editData);
  const navigate = useNavigate();
  const [medications, setMedications] = useState([]);
  const [dateValue, setDateValue] = useState(new Date());
  const [status, setStatus] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [summary, setSummary] = useState("");

  const handleOpenTreatmentModal = () => {
    if (isEditMode && editData?.prescriptions) {
      const prescription = editData.prescriptions;

      triggerGetTreatmentPlans({
        appointmentID: prescription.appointmentID,
        patientUserUniqueID: prescription.userUniqueID,
        userClinicID: prescription.userClinicID,
      });
    }

    setShowTreatmentModal(true);
  };



  const handleCloseTreatmentModal = () => setShowTreatmentModal(false);

  const [testData, setTestData] = useState([]);

  const [addPrescription] = useAddPrescriptionMutation();
  const [addTreatmentPlan] = useAddTreatmentPlanMutation();

  const [fetchBookedPatients, { data: patientListData = [] }] =
    useLazyGetBookedPatientsPrescriptionDropdownQuery();

  const [triggerGetTreatmentPlans, { data: planData }] = useLazyGetTreatmentPlansQuery();

  // Fetch treatment plan summary for edit
  useEffect(() => {
    if (planData?.summary) setSummary(planData.summary);
  }, [planData]);

  // Fetch patient data for non-edit mode
  useEffect(() => {
    if (isEditMode) return;
    const userClinicID = localStorage.getItem("userClinicID");
    const doctorUserUniqueID = localStorage.getItem("userUniqueID");
    const date = appointmentDate || dayjs().format("YYYY-MM-DD");

    if (userClinicID && doctorUserUniqueID && date) {
      fetchBookedPatients({
        userClinicID,
        doctorUserUniqueID,
        appointmentDate: date,
        userUniqueID: patientUserUniqueID || "",
      }).then((res) => {
        if (res?.data?.length > 0) {
          const patient = res.data[0];
          setSelectedPatient({
            ...patient,
            appointmentID: patient.appointmentID || appointmentID || 0,
          });
        }
      });
    }
  }, []);

  // Load edit data
  useEffect(() => {
    if (!editData?.prescriptions) return;

    const { prescriptions, medications = [], testRecommendations = [], patientInfo } = editData;

    const slicedDate = prescriptions.prescriptionDate?.slice(0, 10) || dayjs().format("YYYY-MM-DD");
    setDateValue(new Date(slicedDate));
    setStatus(prescriptions.status || "");

    setSelectedPatient({
      ...patientInfo,
      patientIDUniqueID: prescriptions.patientIDUniqueID,
      doctorID: prescriptions.doctorID,
      doctorUserUniqueID: prescriptions.doctorUserUniqueID,
      userUniqueID: patientInfo?.userUniqueID || prescriptions.userUniqueID,
      userClinicID: prescriptions.userClinicID,
      patientName: patientInfo?.fullName || "",
      appointmentID: prescriptions.appointmentID || appointmentID || 0,
    });

    formik.setValues({
      prescriptionType: prescriptions.prescriptionType || "",
      diagnosis: prescriptions.diagnosis || "",
      notesForPharmacist: prescriptions.notesForPharmacist || "",
    });

    setMedications(medications.map((med, index) => ({ ...med, id: index + 1 })));
    setTestData(
      testRecommendations.map((test) => ({
        name: test.testName,
        priority: test.priority,
        remarks: test.remarks,
      }))
    );
  }, [editData]);

  useEffect(() => {
    if (isEditMode && editData && appointmentID && patientUserUniqueID && userClinicID) {
      triggerGetTreatmentPlans({ appointmentID, patientUserUniqueID, userClinicID });
    }
  }, [isEditMode, editData, appointmentID, patientUserUniqueID, userClinicID]);

  const handleTestChange = (index, field, value) => {
    const updatedTests = [...testData];
    updatedTests[index][field] = value;
    setTestData(updatedTests);
  };

  const handleAddTest = () => setTestData([...testData, { name: "", priority: "", remarks: "" }]);
  const handleDeleteTest = (index) => setTestData(testData.filter((_, i) => i !== index));

  const addMedication = () => setMedications((prev) => [...prev, { id: prev.length + 1 }]);
  const deleteMedication = (idToRemove) => setMedications((prev) => prev.filter((med) => med.id !== idToRemove));



  // Save treatment plan + prescription
  const handleSaveTreatmentPlan = async () => {
    // Filter only required medications/tests for "In Progress"
    const filteredMedications =
      status === "In Progress"
        ? medications.filter((med) => med.name?.trim() || med.dose?.trim() || med.frequency?.trim())
        : medications;

    const filteredTests =
      status === "In Progress"
        ? testData.filter((test) => test.name?.trim() || test.priority?.trim() || test.remarks?.trim())
        : testData;

    const prescriptionPayload = {
      prescriptionInfo: {
        userUniqueID: selectedPatient?.userUniqueID || "",
        userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID") || "",
        patientIDUniqueID: selectedPatient?.patientIDUniqueID || "",
        prescriptionDate: new Date(dateValue).toISOString(),
        prescriptionType: formik.values.prescriptionType,
        diagnosis: formik.values.diagnosis,
        notesForPharmacist: formik.values.notesForPharmacist,
        isActive: true,
        status: status || "Open",
        doctorID: selectedPatient?.doctorID || 0,
        doctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
        appointmentID: selectedPatient?.appointmentID || 0,
        notifyPatient: true,
        markAsUrgent: true,
      },
      medicationInfo: filteredMedications.map((med) => ({
        ...med,
        userUniqueID: selectedPatient?.userUniqueID,
        userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID") || "",
        patientIDUniqueID: selectedPatient?.patientIDUniqueID || "",
        doctorID: selectedPatient?.doctorID || 0,
        doctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
        appointmentID: selectedPatient?.appointmentID || 0,
        isActive: true,
      })),
      testRecommendationInfo: filteredTests.map((test) => ({
        testName: test.name,
        priority: test.priority,
        remarks: test.remarks,
        userUniqueID: selectedPatient?.userUniqueID,
        userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID") || "",
        patientIDUniqueID: selectedPatient?.patientIDUniqueID || "",
        doctorID: selectedPatient?.doctorID || 0,
        doctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
        appointmentID: selectedPatient?.appointmentID || 0,
        isActive: true,
      })),
    };

    const treatmentPayload = {
      patientUserUniqueID: selectedPatient?.userUniqueID,
      doctorUserUniqueID: selectedPatient?.doctorUserUniqueID,
      userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID"),
      appointmentID: selectedPatient?.appointmentID,
      summary,
      updatedBy: "Doctor",
    };

    try {
      const treatmentRes = await addTreatmentPlan(treatmentPayload);
      const prescriptionRes = await addPrescription(prescriptionPayload);

      if (
        [100, 200].includes(treatmentRes?.data?.result_Code) &&
        [100, 200].includes(prescriptionRes?.data?.result_Code)
      ) {
        toast.success("Prescription saved successfully", { autoClose: 3000 });
        setTimeout(() => navigate("/Prescription"), 2000);
      } else {
        toast.warn("Something went wrong", { autoClose: 3000 });
      }
    } catch (err) {
      console.error("Saving failed", err);
    } finally {
      handleCloseTreatmentModal();
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      prescriptionType: "",
      diagnosis: "",
      notesForPharmacist: "",
    },
    onSubmit: async (values) => {
      if (!status) {
        toast.warn("Status is required", { autoClose: 3000 });
        return;
      }

      // Filter only required medications/tests for "In Progress"
      const filteredMedications =
        status === "In Progress"
          ? medications.filter((med) => med.name?.trim() || med.dose?.trim() || med.frequency?.trim())
          : medications;

      const filteredTests =
        status === "In Progress"
          ? testData.filter((test) => test.name?.trim() || test.priority?.trim() || test.remarks?.trim())
          : testData;

      const payload = {
        prescriptionInfo: {
          userUniqueID: selectedPatient?.userUniqueID || "",
          userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID") || "",
          patientIDUniqueID: selectedPatient?.patientIDUniqueID || "",
          prescriptionDate: new Date(dateValue).toISOString(),
          prescriptionType: values.prescriptionType,
          diagnosis: values.diagnosis,
          notesForPharmacist: values.notesForPharmacist,
          isActive: true,
          status: status || "Open",
          doctorID: selectedPatient?.doctorID || 0,
          doctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
          appointmentID: selectedPatient?.appointmentID || 0,
          notifyPatient: true,
          markAsUrgent: true,
        },
        medicationInfo: filteredMedications.map((med) => ({
          ...med,
          userUniqueID: selectedPatient?.userUniqueID,
          userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID") || "",
          patientIDUniqueID: selectedPatient?.patientIDUniqueID || "",
          doctorID: selectedPatient?.doctorID || 0,
          doctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
          appointmentID: selectedPatient?.appointmentID || 0,
          isActive: true,
        })),
        testRecommendationInfo: filteredTests.map((test) => ({
          testName: test.name,
          priority: test.priority,
          remarks: test.remarks,
          userUniqueID: selectedPatient?.userUniqueID,
          userClinicID: selectedPatient?.userClinicID || localStorage.getItem("userClinicID") || "",
          patientIDUniqueID: selectedPatient?.patientIDUniqueID || "",
          doctorID: selectedPatient?.doctorID || 0,
          doctorUserUniqueID: selectedPatient?.doctorUserUniqueID || "",
          appointmentID: selectedPatient?.appointmentID || 0,
          isActive: true,
        })),
      };

      try {
        const response = await addPrescription(payload);
        if ([100, 200].includes(response?.data?.result_Code)) {
          toast.success("Prescription saved successfully", { autoClose: 3000 });
          setTimeout(() => navigate("/Prescription"), 2000);
        } else {
          toast.warn("Something went wrong", { autoClose: 3000 });
        }
      } catch (err) {
        console.error("Submission failed", err);
      }
    },
  });

  const presstyles = `
  .test-recommendations select {
  width: 100%;
  padding: 0.5rem;
  margin-top: 0.25rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.test-recommendations .form-group {
  margin-bottom: 1rem;
}
  .prescription-layout {
  display: flex;
  gap: 2rem;
  padding: 2rem;
  background: #f9fafb;
  // margin-bottom: 4rem;
}
 
.left {
  flex: 2;
}
 
.right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
 
.card {
  background: #fff;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 0 5px rgba(0,0,0,0.05);
}
 
h2 {
  margin-bottom: 0.5rem;
}
 
.grid-2 {
  display: flex;
  gap: 1rem;
}
 
input, textarea, select {
  width: 100%;
  padding: 0.5rem;
  margin-top: 0.3rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 6px;
}
 
button {
  cursor: pointer;
}
 
.btn-submit {
  background: #111;
  color: #fff;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
}
 
.btn-cancel {
  background: #e5e7eb;
  padding: 0.6rem 1.2rem;
  margin-right: 1rem;
  border: none;
  border-radius: 6px;
}
 
.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}
  .medications-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
  .template-selector {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
 
.template-selector label {
  flex: 1;
  font-weight: 500;
}
 
.template-selector select {
  flex: 2;
  max-width: 300px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 6px;
}
  .form-group select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.95rem;
  margin-bottom: 1rem;
}
 
 
  .sx-react-calendar-wrapper{
    width: 100%;
    max-height: 38rem;
  }
 
  .sx__view-selection{
    display: none;
  }
 
 
 
  .sx__week-grid__date-axis {
    padding-left: 1.5rem;
    display: flex;
}
 
.sx__week-grid__date
 {
    flex: unset;
    display: flex;
    flex-flow: column;
    align-items: center;
    padding: var(--sx-spacing-padding3) 0;
    gap: var(--sx-spacing-padding1);
}
 
.sx__view-container {        /* hides both scrollbars and scrolling */
  scrollbar-width: none;    /* hides scrollbar in Firefox */
  -ms-overflow-style: none; /* hides scrollbar in IE and Edge */
}
 
/* hides scrollbar in WebKit browsers like Chrome and Safari */
.sx__view-container::-webkit-scrollbar {
  display: none;
}
 
.sx__calendar{
  border: unset;
  border-radius: unset;
}
 
.cust-bg-tint{
  background-color: #8080800d;
}
 
.styled-label {
  font-size: .8rem;
  font-weight: 600;
  margin-bottom: .2rem;
  color: #6b6b6b;
}
 
input::placeholder {
  font-weight: 400;
  font-size: .8rem;
  opacity: 0.3;
  color: #adadad;
}
 
.rs-picker-toggle-placeholder {
  font-weight: 400;
  font-size: .8rem;
  color: #73717161 !important;
}
 
.rs-picker-popup.rs-picker-popup-date{
  z-Index : 10000;
}
 
.bdr-styled-container{
  border: 1px solid #8080804a;
  border-radius: 5px;
  min-height: 7rem;
}
 
.rs-input-group.rs-input-group-inside {
height: 40px;
padding: 8px;
margin-top: 5px;
}
 
.rs-input-group .rs-form-control-wrapper~.rs-input-group-addon, .rs-input-group input.rs-input~.rs-input-group-addon{
  color: #7eb18c;
  background-color: transparent;
}
 
.rs-picker-default .rs-picker-toggle.rs-btn .rs-picker-toggle-indicator .rs-picker-caret-icon, .rs-picker-default .rs-picker-toggle.rs-btn .rs-picker-toggle-indicator .rs-picker-clean{
  color: #7eb18c;
}
 
.rs-btn-ghost.rs-btn-active, .rs-btn-ghost:active {
  background-color: transparent;
  border-color: #004299;
  border-color: #7eb18c;
  color: #004299;
  color: #7eb18c;
}
 
.rs-btn-ghost:focus, .rs-btn-ghost:hover {
  background-color: transparent;
  border-color: #7eb18c;
  border-color: #7eb18c;
  -webkit-box-shadow: 0 0 0 1px #0a5dc2;
  -webkit-box-shadow: 0 0 0 1px var(--rs-btn-ghost-hover-border);
  box-shadow: 0 0 0 1px #0a5dc2;
  box-shadow: 0 0 0 1px #7eb18c;
  color: #7eb18c;
  color: #7eb18c;
}
 
.sx__week-grid__hour-text {
  color: #7eb18c;
}
.sx__week-grid__date--is-today .sx__week-grid__date-number{
  background-color: #7eb18c;
}
 
.sx__date-picker__day.sx__date-picker__day--today{
  background-color: #7eb18c;
}
 
.sx__date-picker__month-view-header__month-year:hover{
  color: #7eb18c;
}
 
.sx__date-picker__day.sx__date-picker__day--selected {
  background-color: #7eb18c6e;
  color: #fffefe;
}
 
.sx__date-input--active .sx__date-input {
  border-color: #7eb18c;
  outline: 1px solid #7eb18c;
}
 
.sx__date-input--active .sx__date-input-label{
  color: #7eb18c;
}
 
.sx__week-grid__date--is-today .sx__week-grid__day-name{
  color: #7eb18c;
}
 
.sx__is-calendar-small .sx__range-heading{
  color: #7eb18c;
}
 
.sx__time-grid-event{
  background-color: #d7f3df !important;
  color: #4a6d53 !important;
  border-top: 0px;
  border-inline-end: 0px;
  border-bottom: 0px;
  border-inline-start: 4px solid #4a6d53 !important;
}
 
 
.aptmnt-svbtn{
  background-color: transparent;
  border: 1px solid #7eb18c;
  color: #7eb18c;
}
 
.aptmnt-svbtn:hover{
  background-color: #7eb18c;
    color: white;
    border: 1px solid #7eb18c;
}
 
.rs-dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
  width : 100%;
}
 
.scrollable-dropdown .rs-dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
}
  .medications-scroll {
   max-height: 450px;   /* adjust as needed */
  overflow-y: auto;
   padding-right: 8px; /* avoid scrollbar overlap */
}
.custom-dropdown .rs-btn {
  background-color: #fff;
  border: 1px solid #e6e6eb;
  border-radius: 6px;
  height: 38px;
  font-size: 14px;
  color: #6c757d; /* placeholder-like color */
  padding-left: 12px;
  text-align: left;
  width: 100%;
  box-shadow: none;
}
 
.custom-dropdown .rs-btn:focus,
.custom-dropdown .rs-btn:active,
.custom-dropdown .rs-btn:hover {
  border-color: #86b7fe;
}
 
.custom-dropdown .rs-dropdown-toggle-caret {
  margin-left: auto;
}
  .block-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
          }
 
          .block-header h5 {
            font-size: 1.2rem;
            font-weight: 600;
          }
 
          .delete-btn {
            background: none;
            border: none;
            font-size: 1.2rem;
            color: #e74c3c;
            cursor: pointer;
          }
 
          .test-header{
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          }
 
`;

  return (
    <>
      <style>{presstyles}</style>
      <div className="row">
        <div
          style={{ borderBottom: "1px dashed gray", paddingBottom: "1rem" }}
          className="col-12 mb-4 d-flex justify-content-between"
        >
          <div className="d-flex justify-content-between">
            <div>
              <h2 className="h3 fw-bold">Prescription</h2>
              {/* <p className="text-secondary">
                Create a new prescription for a patient.
              </p> */}
            </div>
          </div>
        </div>
      </div>
      <div className="prescription-layout">
        <div className="left">
          <div className="card">
            <div className="grid-2">
              <div>
                <label>Prescription Date</label>
                <DatePicker
                  value={dateValue}
                  onChange={setDateValue}
                  format="dd-MMM-yyyy"
                  placeholder="dd-mmm-yyyy"
                  oneTap
                  style={{ width: "100%", height: "33px", borderRadius: "4px" }}
                  disabled={isEditMode}
                />
              </div>
              <div className="form-group">
                <label>Prescription Type</label>
                <select
                  name="prescriptionType"
                  value={formik.values.prescriptionType}
                  onChange={formik.handleChange}
                  disabled={isEditMode}
                  className={isEditMode ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}
                >
                  <option value="">Select type</option>
                  <option value="standard">Standard</option>
                  <option value="nonStandard">Non-Standard</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>
            </div>
            <label>Diagnosis</label>
            <textarea
              name="diagnosis"
              value={formik.values.diagnosis}
              onChange={formik.handleChange}
              className="bg-white"
              placeholder="Enter diagnosis or reason for prescription"
            />

         
          </div>

          <div className="card">
            <div className="medications-header">
              <h4>Medications</h4>
              <button className="gradient-button" onClick={addMedication}>
                + Add Medication ({medications.length})
              </button>
            </div>

            {/* Scroll only this section */}
            <div className="medications-scroll">
              {medications.map((med, idx) => (
                <MedicationBlock
                  key={med.id}
                  index={idx + 1}
                  data={med}
                  isEditMode={isEditMode}
                  onChange={(field, value) => {
                    const updated = [...medications];
                    updated[idx][field] = value;
                    setMedications(updated);
                  }}
                  onDelete={() => deleteMedication(med.id)}
                />
              ))}
            </div>
          </div>

          <div className="card">
            <div className="test-recommendations">
              <div className="test-header">
                <h4 className="">Test Recommendations</h4>
                <button className="gradient-button" onClick={handleAddTest}>
                  + Add Test ({testData.length})
                </button>
              </div>
              <div className="medications-scroll">
                {testData.map((test, index) => (
                  <div
                    key={index}
                    className="test-entry"
                    style={{
                      padding: "1rem",
                      marginBottom: "1rem",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                    }}
                  >
                    <div className="block-header">
                      <h5>Test #{index + 1}</h5>
                      <button
                        className="delete-btn"
                        title="Remove Medication"
                        onClick={() => handleDeleteTest(index)}
                      >
                        <MdOutlineDeleteOutline color={"red"} />
                      </button>
                    </div>
                    <div
                      className="row"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <div className="form-group col-md-5">
                        <label>Test Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Blood Test"
                          value={test.name}
                          onChange={(e) =>
                            handleTestChange(index, "name", e.target.value)
                          }
                        />
                      </div>

                      <div className="form-group col-md-6">
                        <label>Priority</label>
                        <select
                          value={test.priority}
                          onChange={(e) =>
                            handleTestChange(index, "priority", e.target.value)
                          }
                        >
                          <option value="">Select Priority</option>
                          <option value="Routine">Routine</option>
                          <option value="Urgent">Urgent</option>
                          <option value="Stat">Stat</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group p-2">
                      <label>Remarks</label>
                      <input
                        type="text"
                        placeholder="e.g. Fasting required"
                        value={test.remarks}
                        onChange={(e) =>
                          handleTestChange(index, "remarks", e.target.value)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <h4 className="h4">Additional Information</h4>
            <label>Notes for Pharmacist</label>
            <textarea
              name="notesForPharmacist"
              value={formik.values.notesForPharmacist}
              onChange={formik.handleChange}
              placeholder="Enter any additional notes"
            />
           
<div className="relative w-60 flex items-center gap-2">
  <select
    value={status}
    name="status"
    onChange={(e) => setStatus(e.target.value)}
    className="appearance-none w-full px-4 py-2 pr-10 border border-gray-300 rounded-md text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
  >
    <option value="" disabled hidden>
      Select Status
    </option>
    <option value="Completed">Completed</option>
    <option value="In Progress">In Progress</option>
  </select>

  {/* Info Icon with Popover */}
  <Whisper
    placement="top"
    trigger="hover"
    speaker={
      <Popover style={{ borderRadius: '10px', maxWidth: '320px', padding: '16px' }}>
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="font-semibold text-gray-800 text-sm">In Progress</span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed pl-4">
              Lab reports, tests, or diagnosis procedures are still ongoing.
            </p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="font-semibold text-gray-800 text-sm">Completed</span>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed pl-4">
              Diagnosis is finished and prescription has been issued.
            </p>
          </div>
        </div>
      </Popover>
    }
  >
    <div className="cursor-help">
      <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
    </div>
  </Whisper>
</div>
          </div>

          <div className="form-actions">
            <button className="btn-back"
              onClick={() => navigate(-1)}

              style={{ marginRight: "10px" }}>


              Cancel
            </button>
            {status === "Completed" ? (
              <button
                type="button"
                className="gradient-button"
                onClick={handleOpenTreatmentModal}
              >
                Make Treatment Plan
              </button>
            ) : (
              <button
                type="button"
                onClick={formik.handleSubmit}
                className="gradient-dark-button"
              >
                Create Prescription
              </button>
            )}
          </div>

        </div>

        <div className="right">
  <PatientInfoCard
    patientList={patientListData}
    selectedPatient={selectedPatient}
    setSelectedPatient={setSelectedPatient}
    isEditMode={isEditMode}
  />

  {/* ✅ Prescribed Medications */}
  {medications.length > 0 && (
    <div className="card">
      <h4 className="mb-2">Prescribed Medications</h4>
      <ul>
        {medications.map((med, idx) => (
          <li key={idx} style={{ marginBottom: "0.5rem" }}>
            <strong>
              {`Medication #${idx + 1}`} — {med.medicationName || "Unnamed"}
            </strong>
            {med.dosageValue && med.dosageUnit && (
              <> — {med.dosageValue}
                {med.dosageUnit}
              </>
            )}
            {med.frequency && <> — {med.frequency}</>}
            {med.duration && med.durationUnit && (
              <> — for {med.duration} {med.durationUnit}</>
            )}
          </li>
        ))}
      </ul>
    </div>
  )}

  {/* ✅ Preview Test Recommendations */}
  {testData.length > 0 && (
    <div className="card">
      <h4 className="mb-2">Test Recommendations</h4>
      <ul>
        {testData.map((test, idx) => (
          <li key={idx} style={{ marginBottom: "0.5rem" }}>
            <strong>Test #{idx + 1}</strong>
            {test.name && <> — {test.name}</>}
            {test.priority && <> — {test.priority}</>}
            {test.remarks && <> — ({test.remarks})</>}
          </li>
        ))}
      </ul>
    </div>
  )}
</div>


      </div>

      {showTreatmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                Treatment Plan Summary
              </h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Summary
                </label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Write treatment summary here..."
                  className="w-full h-64 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleCloseTreatmentModal}
                className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTreatmentPlan}
                disabled={!summary.trim()}
                className="px-6 py-2 gradient-button text-white rounded-lg font-medium hover:shadow-lg transition-all duration-200"
              >
                Save Plan
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={30000} />
    </>
  );
}

export default ClinicPrescriptionForm;